import { parseWithZod } from '@conform-to/zod'
import { invariantResponse } from '@epic-web/invariant'
import {
	json,
	type LoaderFunctionArgs,
	type ActionFunctionArgs,
	type HeadersFunction,
	type LinksFunction,
	type MetaFunction,
} from '@remix-run/node'
import {
	Form,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useFetchers,
	useLoaderData,
} from '@remix-run/react'
import { withSentry } from '@sentry/remix'
import { useRef } from 'react'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import CompleteSidebar from './components/completeSidebar.tsx'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { Button } from './components/ui/button.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuTrigger,
} from './components/ui/dropdown-menu.tsx'
import { Icon, href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { Toaster } from './components/ui/toaster.tsx'
import ThemeContextProvider from './contexts/ThemeContext.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints, useHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl, getUserImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { useRequestInfo } from './utils/request-info.ts'
import { type Theme, setTheme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { useOptionalUser, useUser, userHasRole } from './utils/user.ts'

export const links: LinksFunction = () => {
	return [
		{
			rel: 'preload',
			href: 'https://oui-restaurants.com/public/img/Ouitech+Logo.png',
			as: 'image',
		},
		{
			rel: 'preload',
			href: 'https://oui-restaurants.com/public/img/Ouitech+Logo.png',
			as: 'image',
		},
		{
			property: 'og:type',
			content: 'website',
		},
		{
			property: 'og:description',
			content:
				'Create recipes from your videos and build your own profesionally designed website',
		},
		{
			property: 'og:title',
			content: 'Oui Chef',
		},
		{
			property: 'og:image',
			content: 'https://www.chefskiss.co.uk/images/chefs2.png',
		},
		{
			property: 'og:url',
			content: 'https://oui-restaurants.com/public/img/Ouitech+Logo.png',
		},
		{
			name: 'twitter:card',
			content: 'summary',
		},
		{
			name: 'twitter:site',
			content: '@marcusps1',
		},
		{
			name: 'twitter:title',
			content: 'Oui Chef',
		},
		{
			name: 'twitter:description',
			content:
				'Make your restaurant stand out with our recipe pricing software',
		},
		{
			name: 'twitter:image',
			content: 'https://oui-restaurants.com/public/img/Ouitech+Logo.png',
		},
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		// Preload CSS as a resource to avoid render blocking
		{ rel: 'preload', href: tailwindStyleSheetUrl, as: 'style' },
		{ rel: 'mask-icon', href: '/favicons/favicon.png' },
		{
			rel: 'alternate icon',
			type: 'image/png',
			href: '/favicons/favicon.png',
		},
		{ rel: 'apple-touch-icon', href: '/favicons/favicon.png' },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
		},
		//These should match the css preloads above to avoid css as render blocking resource
		{ rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon.png' },
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
		{
			rel: 'preconnect',
			href: 'https://fonts.googleapis.com',
		},
		{
			rel: 'preconnect',
			href: 'https://fonts.gstatic.com',
		},
		{
			rel: 'stylesheet',
			href: 'https://fonts.googleapis.com/css2?family=Abhaya+Libre:wght@400;500;600;700;800&family=Abyssinica+SIL&family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap',
		},
	].filter(Boolean)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	return [
		{
			title: data
				? 'Oui Chef - Recipe pricing software'
				: 'Error | Oui Chef',
		},
		{
			name: 'description',
			content: `Recipe pricing software`,
		},
	]
}

// Loads the required user if he or she signed in before
export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUniqueOrThrow({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { id: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = honeypot.getInputProps()

	return json(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
	}
	return headers
}

const ThemeFormSchema = z.object({
	theme: z.enum(['system', 'light', 'dark']),
})

// An action to change the theme and save it in a cookie
export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, {
		schema: ThemeFormSchema,
	})

	invariantResponse(submission.status === 'success', 'Invalid theme received')

	const { theme } = submission.value

	const responseInit = {
		headers: { 'set-cookie': setTheme(theme) },
	}
	return json({ result: submission.reply() }, responseInit)
}

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string>
}) {
	return (
		<html
			lang="en"
			className={`${theme} h-full overflow-x-hidden transition-all duration-500 ease-in-out`}
		>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				<Links />
				<script
					async
					src="https://www.googletagmanager.com/gtag/js?id=G-7T8TNW4NBX"
				></script>
				<script
					dangerouslySetInnerHTML={{
						__html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'G-7T8TNW4NBX');
            `,
					}}
				/>
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
				<Toaster />
			</body>
		</html>
	)
}

function App() {
	// Getting loaders, fetchers and matches
	const data = useLoaderData<typeof loader>()
	const nonce = useNonce()
	const user = useOptionalUser()

	useToast(data.toast)

	return (
		<Document nonce={nonce} theme="light" env={data.ENV}>
			<div className="flex h-screen flex-col justify-between">
				{user && (
					<>
						(
						<header className="mb-2 w-full bg-muted p-4">
							<nav className="flex items-center justify-between">
								<Link to="/">
									<h1 className="text-xl font-bold"></h1>
								</Link>

								<div className="flex items-center justify-center">
									<UserDropdown />

									{/* The theme switcher */}
								</div>
							</nav>
						</header>
						<CompleteSidebar />)
					</>
				)}
				{!user && (
					<>
						<header className="mb-2 w-full bg-muted p-4">
							<nav className="flex items-center justify-between">
								<Link to="/">
									<h1 className="text-xl font-bold">Oui Technologies</h1>
								</Link>

								<div className="flex items-center justify-center">
									<DropdownNotLoggedIn />
								</div>
							</nav>
						</header>
						<Outlet />
					</>
				)}

				<div className="flex-1"></div>

				<Logo />
			</div>
			<EpicToaster closeButton position="top-center" theme="light" />
			<EpicProgress />
		</Document>
	)
}

function Logo() {
	return (
		<Link to="/" className="group grid leading-snug">
			<span className="font-light transition group-hover:-translate-x-1"></span>
			<span className="font-bold transition group-hover:translate-x-1"></span>
		</Link>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<ThemeContextProvider>
				<App />
			</ThemeContextProvider>
		</HoneypotProvider>
	)
}

export default withSentry(AppWithProviders)

function UserDropdown() {
	const user = useUser()
	const formRef = useRef<HTMLFormElement>(null)
	const role = userHasRole(user, 'THERAPIST') //Role focused on is therapist, if not your a customer

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button asChild variant="ghost">
					<Link
						to={`/users/${user.username}`}
						onClick={e => e.preventDefault()}
						className="flex items-center gap-2"
					>
						<img
							className="h-8 w-8 rounded-full object-cover"
							alt={user.name ?? user.username}
							src={getUserImgSrc(user.image?.id)}
						/>
						<span className="text-body-sm font-bold">
							{user.name ?? user.username}
						</span>
					</Link>
				</Button>
			</DropdownMenuTrigger>

			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="start">
					{/* Profile settings */}
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/users/${user.username}`}>
							<Icon className="text-body-md" name="avatar">
								Profile
							</Icon>
						</Link>
					</DropdownMenuItem>

					{/* Bookings link */}
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={'/recipes'}>
							<Icon className="text-body-sm" name="phone">
								My Recipes
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={'/dishes'}>
							<Icon className="text-body-sm" name="phone">
								My Dishes
							</Icon>
						</Link>
					</DropdownMenuItem>

					<DropdownMenuItem asChild>
						<Link prefetch="intent" to="/add-inventory">
							<Icon className="text-body-sm" name="clock">
								Add Inventory
							</Icon>
						</Link>
					</DropdownMenuItem>

					<DropdownMenuItem asChild>
						<Link prefetch="intent" to="/add-inventory/missing">
							<Icon className="text-body-sm" name="clock">
								Missing Inventory
							</Icon>
						</Link>
					</DropdownMenuItem>

					<DropdownMenuItem asChild>
						<Link prefetch="intent" to="/inventory">
							<Icon className="text-body-sm" name="laptop">
								Inventory
							</Icon>
						</Link>
					</DropdownMenuItem>

					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={'/menus'}>
							<Icon className="text-body-sm" name="question-mark-circled">
								Menus
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link
							prefetch="intent"
							to={role ? '/create-recipe' : '/create-recipe'}
						>
							<Icon className="text-body-sm" name="question-mark-circled">
								{/* set avaialbilty as a therapist then that therapist shoudld have avalability in /bookings */}
								Create recipe
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Form action="/logout" method="POST" ref={formRef}>
							<button type="submit">
								<Icon className="text-body-sm" name="exit">
									Logout
								</Icon>
							</button>
						</Form>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}

function DropdownNotLoggedIn() {
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button asChild variant="ghost">
					<span>Menu</span>
				</Button>
			</DropdownMenuTrigger>

			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="start">
					{/* Bookings link */}
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={'/login'}>
							<Icon className="text-body-sm" name="avatar">
								Login
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={'/signup'}>
							<Icon className="text-body-sm" name="plus">
								Signup
							</Icon>
						</Link>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}

/**
 * A function that reads the current user's theme preference, or the one they want to switch to
 *
 * Use this function if you want to read the user's theme
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
export function useTheme() {
	const hints = useHints()
	const requestInfo = useRequestInfo()
	const optimisticMode = useOptimisticThemeMode('/')
	if (optimisticMode) {
		return optimisticMode === 'system' ? hints.theme : optimisticMode
	}
	return requestInfo.userPrefs.theme ?? hints.theme
}

/**
 * If the user's changing their theme mode preference, this will return the
 * value it's being changed to.
 *
 * Use this function when you want to change the theme
 * @param path The URL where the fetcher is located, so as to get the current submission
 * @returns The recently submitted value if the fetcher is found and a value is submitted, or null
 */
export function useOptimisticThemeMode(path: string) {
	const fetchers = useFetchers()
	const themeFetcher = fetchers.find(f => f.formAction === path)

	if (themeFetcher && themeFetcher.formData) {
		const submission = parseWithZod(themeFetcher.formData, {
			schema: ThemeFormSchema,
		})

		if (submission.status === 'success') {
			return submission.value.theme
		}
	}
}

export function ErrorBoundary() {
	// the nonce doesn't rely on the loader so we can access that
	const nonce = useNonce()

	// NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
	// likely failed to run so we have to do the best we can.
	// We could probably do better than this (it's possible the loader did run).
	// This would require a change in Remix.

	// Just make sure your root route never errors out and you'll always be able
	// to give the user a better UX.

	return (
		<Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>
	)
}
